import { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import {
  findLayoutGroup,
  getLayoutBlockData,
  regionsToPixels,
  type PixelRect,
} from 'modules/manager/domain/layout-blocks';

/**
 * Replace the decorative layout-block group with real background images sized
 * to each region. URLs are matched positionally — `urls[i]` fills region `i`.
 * Missing URLs leave that region empty. Loaded images are inserted at the back
 * of the canvas so authored objects render on top.
 */
export async function applyLayoutBackgrounds(editor: FabricEditor, urls: (string | undefined)[]): Promise<void> {
  const canvas = editor.canvas;
  const layoutGroup = findLayoutGroup(canvas);
  if (!layoutGroup) return;
  const data = getLayoutBlockData(layoutGroup);
  if (!data) return;
  const preset = editor.layoutBlocks?.listPresets().find((p) => p.id === data.presetId);
  if (!preset) return;

  const rects = regionsToPixels(preset, editor.resolution);
  const opacity = Math.max(0, Math.min(1, Number.isFinite(data.opacity) ? data.opacity : 1));
  canvas.remove(layoutGroup);

  const loadTasks = rects.map(async (rect, i) => {
    const url = urls[i];
    if (!url) return;
    const fitMode = data.blocks[i]?.fitMode ?? 'cover';
    const image = await loadImage(url);
    if (!image) return;
    fitImageToRect(image, rect, fitMode);
    image.set({ opacity });
    canvas.add(image);
    canvas.sendToBack(image);
  });
  await Promise.all(loadTasks);
  editor.grid?.rebuild();
  canvas.requestRenderAll();
}

function loadImage(url: string): Promise<fabric.Image | null> {
  return new Promise((resolve) => {
    fabric.Image.fromURL(url, (img) => {
      const element = img && (img.getElement?.() as HTMLImageElement | null);
      const ok = element && element.naturalWidth > 0 && element.naturalHeight > 0;
      if (!ok) {
        // eslint-disable-next-line no-console
        console.warn('[applyLayoutBackgrounds] failed to load:', url);
        resolve(null);
        return;
      }
      resolve(img);
    }, { crossOrigin: 'anonymous' });
  });
}

function fitImageToRect(image: fabric.Image, rect: PixelRect, fitMode: 'contain' | 'cover'): void {
  const element = image.getElement() as HTMLImageElement;
  const naturalW = element.naturalWidth || image.width || 1;
  const naturalH = element.naturalHeight || image.height || 1;
  image.set({ width: naturalW, height: naturalH, cropX: 0, cropY: 0 });

  if (fitMode === 'cover') {
    const scale = Math.max(rect.width / naturalW, rect.height / naturalH);
    const cropW = rect.width / scale;
    const cropH = rect.height / scale;
    image.set({
      cropX: Math.max(0, (naturalW - cropW) / 2),
      cropY: Math.max(0, (naturalH - cropH) / 2),
      width: cropW,
      height: cropH,
      scaleX: scale,
      scaleY: scale,
    });
  } else {
    const scale = Math.min(rect.width / naturalW, rect.height / naturalH);
    image.set({ scaleX: scale, scaleY: scale });
  }

  image.set({
    left: rect.left + rect.width / 2,
    top: rect.top + rect.height / 2,
    originX: 'center',
    originY: 'center',
    selectable: false,
    evented: false,
    objectCaching: false,
  });
  image.setCoords();
}
